import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { MdKeyboardArrowRight } from "react-icons/md";
import { toastService } from "@services/ToastService";
import { localFolderService } from "@services/LocalFolderService";
import { Tooltip } from "flowbite-react";
import { FiPlus } from "react-icons/fi";
import Loader from "@shared/components/Loader";
import { createPortal } from "react-dom";

/** ========= Types ========= */

export interface FileOwner {
  first_name?: string;
  last_name?: string;
}

export interface LocalFile {
  id: string;
  filename: string;
  filetype: string;
  size?: number;
  status: string;
  owner?: FileOwner | null;
  uploaded_at?: string | null;
  have_anomalies?: boolean;
  unresolved_anomaly_count?: number;
  /**  used only for scoping uploads to the correct view */
  local_folder_id?: number | null;
}

export interface FolderCreator {
  first_name?: string;
  last_name?: string;
}

export interface LocalFolder {
  id: number;
  name: string;
  created_at?: string | null;
  creator?: FolderCreator | null;
}

/** External components */
export interface LoaderProps {
  color?: string;
}
export type LoaderComponent = React.FC<LoaderProps>;

export interface StatusIconProps<TFile extends LocalFile> {
  file: TFile;
  handleShowPopup: (fileId: string, file?: TFile) => void;
  triggerStatusPopup: (severity: string, anomaly_count: number) => void;
  clearStatusPopup: () => void;
}
export type StatusIconComponent<TFile extends LocalFile> = React.FC<
  StatusIconProps<TFile>
>;

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  containerClassName?: string;
}
export type PaginationComponent = React.FC<PaginationProps>;

export interface BreadcrumbItem {
  id: number;
  name: string;
}

/** Component props */
export interface LocalFilesFoldersProps<TFile extends LocalFile = LocalFile> {
  files: TFile[];
  folders: LocalFolder[];

  projectId: string;
  currentFolderId?: number | null;

  breadcrumbs?: BreadcrumbItem[];
  onNavigateBreadcrumb?: (folderId: number | null) => void;

  onFolderCreated?: (created: LocalFolder) => void | Promise<void>;
  onOpenFileUpload?: () => void;

  searchValue: string;
  onSearchChange: (value: string) => void;

  isVisible: boolean;
  setIsLocalFilesVisible: React.Dispatch<React.SetStateAction<boolean>>;
  onToggleVisible: () => void;

  onOpenFolder: (folderId: number) => void | Promise<void>;
  onLoadFile: (
    id: TFile["id"],
    filetype: string,
    size: number,
    filename: string
  ) => void;

  onShowStatusPopup: (fileId: string, file?: TFile) => void;
  triggerStatusPopup: (severity: string, anomaly_count: number) => void;
  clearStatusPopup: () => void;

  normalizeDate: (dateString: string) => string;
  getFileIcon: (filetype: string) => string;

  loadingFileId?: TFile["id"] | null;
  activeTaskFileIds?: TFile["id"][];
  handleShowPopup: (fileId: string, file?: TFile) => Promise<void>;

  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;

  StatusIcon: StatusIconComponent<TFile>;
  Pagination?: PaginationComponent;

  title?: string;
  searchPlaceholder?: string;
  folderIconSrc?: string;
  sectionIconSrc?: string;
  arrowDownIconSrc?: string;
  arrowUpIconSrc?: string;

  showCheckboxes?: boolean;
  checkedItems: string[];
  setCheckedItems: React.Dispatch<React.SetStateAction<string[]>>;
  onToggleChecked: (id: string) => void;
  onSelectRow: (fileUUID: string) => void;

  /** Upload handler */
  onFilesUploaded: (filesToUpload: File[], folderId?: number) => void;
  refreshOpenedFolder: number;
  setRefreshOpenedFolder: React.Dispatch<React.SetStateAction<number>>;

  isNewItemMenuOpen: boolean;
  setIsNewItemMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isFolderCreating: boolean;
  setIsFolderCreating: React.Dispatch<React.SetStateAction<boolean>>;
  newFolderName: string;
  setNewFolderName: React.Dispatch<React.SetStateAction<string>>;
  isSaving: boolean;
  setIsSaving: React.Dispatch<React.SetStateAction<boolean>>;
}

/** ========= Component ========= */
export function LocalFilesFolders<TFile extends LocalFile>({
  files,
  folders,
  projectId,
  currentFolderId = null,
  breadcrumbs = [],
  onNavigateBreadcrumb,
  onFolderCreated,
  onOpenFileUpload,
  searchValue,
  onSearchChange,
  isVisible,
  setIsLocalFilesVisible,
  onToggleVisible,
  onOpenFolder,
  onLoadFile,
  onShowStatusPopup,
  triggerStatusPopup,
  clearStatusPopup,
  normalizeDate,
  getFileIcon,
  loadingFileId = null,
  activeTaskFileIds = [],
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  StatusIcon,
  Pagination,
  title = "Local Documents",
  searchPlaceholder = "Search document...",
  folderIconSrc = "/folder-icon.svg",
  sectionIconSrc = "/folder.svg",
  arrowDownIconSrc = "/arrow_dropdown_black.svg",
  arrowUpIconSrc = "/arrow_dropdown_black_up.svg",
  showCheckboxes = true,
  checkedItems,
  setCheckedItems,
  onToggleChecked,
  onSelectRow,
  onFilesUploaded,
  setRefreshOpenedFolder,
  handleShowPopup,

  isNewItemMenuOpen,
  setIsNewItemMenuOpen,
  isFolderCreating,
  setIsFolderCreating,
  newFolderName,
  setNewFolderName,
  isSaving,
  setIsSaving,
}: // defaultNewFolderName,
/** delete confirmation callback (optional) */
LocalFilesFoldersProps<TFile>) {
  /** keep a local list for immediate UI updates */
  const [filesToShow, setFilesToShow] = useState<TFile[]>(() => files);

  const [hoveredRow, setHoveredRow] = useState<TFile["id"] | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null); // anchor button
  const portalMenuRef = useRef<HTMLDivElement>(null); // the PORTAL menu element (not the wrapper)
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [coords, setCoords] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  // LocalFilesFolders.ts
  const viewKey = `${projectId}::${currentFolderId ?? "root"}`;
  const prevViewKeyRef = useRef<string>(viewKey);
  const skipCreateBlurRef = useRef(false);

  useEffect(() => {
    const prevKey = prevViewKeyRef.current;

    if (prevKey !== viewKey) {
      prevViewKeyRef.current = viewKey;
      setFilesToShow(files);
      return;
    }

    setFilesToShow((prev) => {
      const incomingById = new Map(files.map((f) => [f.id, f as TFile]));
      const optimistic = prev.filter((p) => !incomingById.has(p.id));
      return [...optimistic, ...(files as TFile[])];
    });
  }, [files, viewKey]);

  const filteredFiles = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    if (!q) return filesToShow;
    return filesToShow.filter((f) =>
      (f.filename || "").toLowerCase().includes(q)
    );
  }, [filesToShow, searchValue]);

  // below filteredFiles useMemo
  const filteredFolders = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    if (!q) return folders;
    return folders.filter((f) => (f.name || "").toLowerCase().includes(q));
  }, [folders, searchValue]);

  const isInteractive = (file: TFile) => {
    const s = (file.status || "").toLowerCase();
    return s === "ready" || s === "failed";
  };

  const ArrowIcon = isVisible ? arrowUpIconSrc : arrowDownIconSrc;

  const showContainer =
    filesToShow.length > 0 ||
    folders.length > 0 ||
    breadcrumbs.length > 0 ||
    isFolderCreating;

  const onRoot = breadcrumbs.length === 0;
  // Positioning
  // --- POSITIONING (runs when menu opens and on next frame to ensure menuRect is measurable) ---
  useLayoutEffect(() => {
    if (!isNewItemMenuOpen) return;
    const anchor = btnRef.current;
    const menu = portalMenuRef.current;
    if (!anchor || !menu) return;

    const place = () => {
      const rect = anchor.getBoundingClientRect();
      const menuRect = menu.getBoundingClientRect();
      setCoords({
        top: rect.bottom + window.scrollY + 8, // 8px gap
        left: rect.right + window.scrollX - menuRect.width, // bottom-end
      });
    };

    // place immediately and again on next frame for safety
    place();
    const raf = requestAnimationFrame(place);
    return () => cancelAnimationFrame(raf);
  }, [isNewItemMenuOpen]);

  // --- REPOSITION on scroll/resize while open ---
  useEffect(() => {
    if (!isNewItemMenuOpen) return;

    const handle = () => {
      const anchor = btnRef.current;
      const menu = portalMenuRef.current;
      if (!anchor || !menu) return;
      const rect = anchor.getBoundingClientRect();
      const menuRect = menu.getBoundingClientRect();
      setCoords({
        top: rect.bottom + window.scrollY + 8,
        left: rect.right + window.scrollX - menuRect.width,
      });
    };

    window.addEventListener("scroll", handle, true);
    window.addEventListener("resize", handle);
    return () => {
      window.removeEventListener("scroll", handle, true);
      window.removeEventListener("resize", handle);
    };
  }, [isNewItemMenuOpen]);

  // --- CLOSE on outside click / ESC ---
  useEffect(() => {
    if (!isNewItemMenuOpen) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsNewItemMenuOpen(false);
    };
    const onClick = (e: MouseEvent) => {
      const menu = portalMenuRef.current;
      const anchor = btnRef.current;
      const target = e.target as Node;
      if (!menu || !anchor) return;
      if (!menu.contains(target) && !anchor.contains(target)) {
        setIsNewItemMenuOpen(false);
      }
    };

    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [isNewItemMenuOpen]);

  // Close on outside click / ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) =>
      e.key === "Escape" && setIsNewItemMenuOpen(false);
    const onClick = (e: MouseEvent) => {
      const menu = menuRef.current;
      const anchor = btnRef.current;
      if (
        menu &&
        anchor &&
        !menu.contains(e.target as Node) &&
        !anchor.contains(e.target as Node)
      ) {
        setIsNewItemMenuOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  // Focus the input as soon as we render the inline create row
  useEffect(() => {
    if (isFolderCreating && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isFolderCreating]);

  const readyFiles = filteredFiles.filter((f) => f.status !== "processing");
  const checkItemsLocal = checkedItems.filter(
    (id) => id.endsWith("-local-file") || id.endsWith("-folder")
  );

  const toggleAll = () => {
    if (checkItemsLocal.length === readyFiles.length + filteredFolders.length) {
      setCheckedItems((prev) =>
        prev.filter(
          (id) => !(id.endsWith("-local-file") || id.endsWith("-folder"))
        )
      );
    } else {
      const fileIds = readyFiles.map((f) => `${f.id}-local-file`);
      const folderIds = filteredFolders.map((f) => `${f.id}-folder`);
      setCheckedItems((prev) => [...prev, ...fileIds, ...folderIds]);
    }
  };

  const allChecked =
    checkItemsLocal.length !== 0
      ? checkItemsLocal.length === readyFiles.length + filteredFolders.length
      : false;

  const defaultNewFolderName = useMemo(
    () => `Folder (${folders.length + 1})`,
    [folders.length]
  );

  const startCreateFolder = () => {
    setIsNewItemMenuOpen(false);
    setIsLocalFilesVisible(true);
    if (isSaving) return;
    setNewFolderName(defaultNewFolderName);
    setIsFolderCreating(true);
  };

  const cancelCreateFolder = () => {
    if (isSaving) return;
    setIsFolderCreating(false);
    setNewFolderName("");
    skipCreateBlurRef.current = false;
  };

  const confirmCreateFolder = async () => {
    if (isSaving) return;

    const name = (newFolderName || "").trim() || defaultNewFolderName;
    setIsSaving(true);
    try {
      const created = await localFolderService.create(
        {
          name,
          project_id: projectId,
          parent_id: currentFolderId ?? null,
        },
        projectId
      );

      toastService.success("Folder created");
      setIsFolderCreating(false);
      setNewFolderName("");
      setIsSaving(false);

      if (onFolderCreated) {
        await onFolderCreated({
          id: created.id,
          name: created.name,
          created_at: created.created_at,
          creator: {
            first_name: created?.creator?.first_name,
            last_name: created?.last_name,
          },
        });
      }
    } catch (err: any) {
      setIsSaving(false);
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to create folder";
      toastService.warn(msg);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  /** Prevent toggling selection when clicking nested interactive elements */
  const isInteractiveTarget = (el: HTMLElement) => {
    const tag = el.tagName.toLowerCase();
    return (
      tag === "a" ||
      tag === "button" ||
      tag === "input" ||
      tag === "label" ||
      el.closest("button, a, input, label") !== null
    );
  };

  /** upload from the current view; append ONLY if the returned file belongs to this view */
  const handleFileInputChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const picked = event.target.files ? Array.from(event.target.files) : [];
    setIsLocalFilesVisible(true);
    setIsNewItemMenuOpen(false);
    await onFilesUploaded(picked, currentFolderId ?? undefined);
    console.log("file uploaded");
    setRefreshOpenedFolder((prev) => prev + 1);
  };

  useEffect(() => {
    setCheckedItems([]);
  }, [viewKey]);

  if (!showContainer) return null;

  return (
    <div className="border-2 border-[#E4E7EC] rounded-lg overflow-hidden">
      {/* Header */}
      <div className="w-full bg-[#F9FAFB] flex items-center justify-between px-4 py-4">
        <div className="flex items-center space-x-3 min-w-0">
          <img
            src={sectionIconSrc}
            className="w-6 flex-shrink-0"
            alt="Documents"
          />
          <div className="flex items-center flex-col min-w-0">
            {onNavigateBreadcrumb && (
              <h2
                className={`text-sm 2xl:text-base items-center flex ${
                  breadcrumbs.length === 0 ? "font-semibold" : "font-normal"
                } text-[#182637] truncate`}
              >
                <button
                  type="button"
                  className={`${
                    !onRoot ? "hover:underline" : ""
                  } focus:outline-none focus:ring-2 rounded px-0.5`}
                  onClick={() => {
                    if (onRoot) return;
                    onNavigateBreadcrumb(null);
                  }}
                  aria-current={currentFolderId === null ? "page" : undefined}
                >
                  {title}
                </button>
                {!onRoot && (
                  <div className="flex items-center text-sm 2xl:text-base text-gray-600 flex-wrap gap-1">
                    {breadcrumbs.map((bc, idx) => (
                      <React.Fragment key={`bc-${bc.id}-${idx}`}>
                        <MdKeyboardArrowRight aria-hidden="true" />
                        <button
                          type="button"
                          className={`hover:underline focus:outline-none focus:ring-2 rounded px-0.5 ${
                            currentFolderId === bc.id
                              ? "font-semibold"
                              : "font-normal"
                          }`}
                          onClick={() => onNavigateBreadcrumb(bc.id)}
                          aria-current={
                            idx === breadcrumbs.length - 1 ? "page" : undefined
                          }
                        >
                          {bc.name}
                        </button>
                      </React.Fragment>
                    ))}
                  </div>
                )}
                <p className="ml-1 text-gray-500 font-normal text-xs 2xl:text-sm">
                  ({filteredFiles.length + filteredFolders.length} documents)
                </p>
              </h2>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* New dropdown */}
          <div className="relative">
            <button
              ref={btnRef}
              type="button"
              className={`flex items-center text-xs gap-2 px-3 h-8 rounded-md border border-gray-200 bg-white font-medium  ${
                isNewItemMenuOpen ? "text-gray-700" : "text-gray-500"
              } hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500`}
              onClick={() => setIsNewItemMenuOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={isNewItemMenuOpen}
            >
              <span className="text-lg leading-none">
                <FiPlus />
              </span>
              New
            </button>

            {isNewItemMenuOpen &&
              createPortal(
                <div
                  ref={portalMenuRef}
                  role="menu"
                  style={{
                    position: "absolute",
                    top: coords.top,
                    left: coords.left,
                    zIndex: 60,
                    width: "11rem",
                  }}
                  className="rounded-md border border-gray-200 bg-white shadow-lg"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    id="fileInput"
                    accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.zip"
                    onChange={async (e) => {
                      await handleFileInputChange(e);
                      setIsNewItemMenuOpen(false);
                    }}
                  />
                  <label
                    htmlFor="fileInput"
                    className="text-md py-2.5 block cursor-pointer hover:bg-gray-100 transition border-b-2 border-gray-100"
                  >
                    <div className="px-3 flex space-x-2 w-28 items-center">
                      <img
                        src="/file-upload.svg"
                        className="w-5 h-5"
                        alt="Upload"
                      />
                      <span className="text-sm 2xl:text-base whitespace-nowrap">
                        File Upload
                      </span>
                    </div>
                  </label>

                  <button
                    type="button"
                    className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-100 flex space-x-2 items-center"
                    onClick={() => {
                      startCreateFolder();
                      setIsNewItemMenuOpen(false);
                    }}
                  >
                    <img
                      src="/new-folder.svg"
                      className="w-5 h-5"
                      alt="New folder"
                    />
                    <span className="text-sm 2xl:text-base whitespace-nowrap">
                      New Folder
                    </span>
                  </button>
                </div>,
                document.body
              )}
          </div>

          {/* Search + collapse */}
          {isVisible && files.length > 0 && (
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              className="border border-gray-200 rounded-md px-3 h-8 w-72 text-sm text-gray-500 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              aria-label="Search documents"
            />
          )}
          <button
            type="button"
            onClick={onToggleVisible}
            aria-expanded={isVisible}
            aria-label={isVisible ? "Collapse documents" : "Expand documents"}
            className="rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:border-transparent"
          >
            <img
              src={ArrowIcon}
              className="w-4"
              alt={isVisible ? "Collapse" : "Expand"}
            />
          </button>
        </div>
      </div>
      {/* Body */}
      {isVisible && (
        <>
          <table className="w-full table-auto text-sm 2xl:text-base">
            <thead>
              <tr className="text-left border-b-2 border-[#f9fafb]">
                <th className="py-3 px-4">
                  {showCheckboxes && (
                    <input
                      type="checkbox"
                      checked={allChecked}
                      onChange={toggleAll}
                      aria-label="Select all items"
                    />
                  )}
                </th>
                <th className="py-3 px-4 text-slate-700">Document</th>
                <th className="py-3 px-4 text-slate-700">Type</th>
                <th className="py-3 px-4 text-slate-700">Size</th>
                <th className="py-3 px-4 text-slate-700">User</th>
                <th className="py-3 px-4 text-slate-700">Date</th>
                <th className="py-3 px-4 text-slate-700 text-center justify-center">
                  Status
                </th>
              </tr>
            </thead>

            <tbody>
              {/* Inline "New Folder" row */}
              {isFolderCreating && (
                <tr className="bg-white border-b-2 border-[#f9fafb]">
                  <td className="py-3 px-4" />
                  <td className="py-3 px-4" colSpan={2}>
                    <div className="flex items-center space-x-3">
                      <img
                        src={folderIconSrc}
                        alt="Folder"
                        className="w-7 h-7 flex-shrink-0"
                      />

                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <input
                          ref={inputRef}
                          type="text"
                          value={newFolderName}
                          disabled={isSaving}
                          onChange={(e) => setNewFolderName(e.target.value)}
                          onKeyDown={async (e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              await confirmCreateFolder();
                            }
                            if (e.key === "Escape") {
                              e.preventDefault();
                              cancelCreateFolder();
                            }
                          }}
                          onBlur={async () => {
                            if (skipCreateBlurRef.current) {
                              skipCreateBlurRef.current = false;
                              return;
                            }
                            await confirmCreateFolder();
                          }}
                          className="flex-1 min-w-0 border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />

                        {/* NEW: inline cancel button */}
                        <button
                          type="button"
                          aria-label="Cancel new folder"
                          title="Cancel"
                          disabled={isSaving}
                          onMouseDown={() => {
                            // mark to skip onBlur auto-confirm *before* the input loses focus
                            skipCreateBlurRef.current = true;
                            cancelCreateFolder();
                          }}
                          className="p-1 rounded hover:bg-gray-100 focus:outline-none disabled:opacity-50"
                        >
                          {/* simple × icon (you can swap for your own svg) */}
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            className="w-5 h-5 text-gray-600"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 8.586 5.707 4.293a1 1 0 1 0-1.414 1.414L8.586 10l-4.293 4.293a1 1 0 1 0 1.414 1.414L10 11.414l4.293 4.293a1 1 0 0 0 1.414-1.414L11.414 10l4.293-4.293a1 1 0 0 0-1.414-1.414L10 8.586Z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </td>
                  <td colSpan={4}></td>
                  <td className="py-3 px-4" />
                </tr>
              )}

              {/* Folders (only on first page) */}
              {filteredFolders.map((folder) => {
                const isChecked = checkedItems.includes(`${folder.id}-folder`);
                const isHovered = hoveredRow === `${folder.id}-folder`;

                return (
                  <tr
                    key={`${folder.id}-local-folder`}
                    className="hover:bg-[#f9fafb]"
                    onMouseEnter={() => setHoveredRow(`${folder.id}-folder`)}
                    onMouseLeave={() => setHoveredRow(null)}
                    role="row"
                    onClick={() =>
                      !isChecked && onToggleChecked(`${folder.id}-folder`)
                    }
                  >
                    <td className="py-3 px-4">
                      {showCheckboxes && (isChecked || isHovered) && (
                        <input
                          type="checkbox"
                          checked={checkedItems.includes(`${folder.id}-folder`)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => {
                            e.stopPropagation();
                            onToggleChecked(`${folder.id}-folder`);
                          }}
                          aria-label={`Select folder ${folder.name}`}
                        />
                      )}
                    </td>

                    <td className="py-3 px-4 text-sm 2xl:text-base">
                      <button
                        type="button"
                        className="flex text-slate-700 items-center my-1 space-x-3 w-full text-left hover:underline"
                        onClick={() => onOpenFolder(folder.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            onOpenFolder(folder.id);
                          }
                        }}
                        aria-label={`Open folder ${folder.name}`}
                      >
                        <img
                          src={folderIconSrc}
                          alt="Folder"
                          className="w-6 h-6 2xl:w-7 2xl:h-7"
                        />
                        <div className="w-full truncate">{folder.name}</div>
                      </button>
                    </td>

                    <td className="py-3 px-4 text-slate-700">Folder</td>
                    <td className="py-3 px-4 text-slate-700">-</td>
                    <td className="py-3 px-4 text-slate-700">
                      {folder.creator
                        ? `${folder.creator.first_name ?? ""} ${
                            folder.creator.last_name ?? ""
                          }`.trim() || "Unknown"
                        : "Unknown"}
                    </td>
                    <td className="py-3 px-4 text-slate-700">
                      {folder.created_at
                        ? normalizeDate(folder.created_at ?? "")
                        : ""}
                    </td>
                    <td className="py-3 px-4 text-slate-700" />
                  </tr>
                );
              })}

              {/* Files */}

              {filteredFiles.map((file) => {
                const interactive = isInteractive(file);
                const rowChecked = checkedItems.includes(
                  `${file.id}-local-file`
                );
                const rowHovered = hoveredRow === `${file.id}-local-file`;
                const baseStatus = (file.status || "").toLowerCase();
                const rowDisabled =
                  baseStatus !== "ready" && baseStatus !== "failed";

                return (
                  <tr
                    key={`${file.id}-local-file`}
                    className={[
                      "transition-colors text-sm 2xl:text-base duration-200 border-b-2 border-[#f9fafb] group",
                      rowDisabled
                        ? "bg-gray-100 text-gray-400 cursor-wait opacity-60"
                        : rowChecked || rowHovered
                        ? "bg-[#f9fafb]"
                        : "bg-white",
                    ].join(" ")}
                    onMouseEnter={() => {
                      if (interactive) setHoveredRow(`${file.id}-local-file`);
                    }}
                    onMouseLeave={() => {
                      if (interactive) setHoveredRow(null);
                    }}
                    onClick={(e) => {
                      if (
                        interactive &&
                        showCheckboxes &&
                        !isInteractiveTarget(e.target as HTMLElement)
                      ) {
                        onSelectRow(`${file.id}-local-file`);
                      }
                    }}
                    tabIndex={interactive ? 0 : -1}
                    onKeyDown={(e) => {
                      if (!interactive) return;
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        if (showCheckboxes)
                          onSelectRow(`${file.id}-local-file`);
                      }
                    }}
                    aria-selected={rowChecked}
                    role="row"
                  >
                    <td className="py-3 px-4">
                      {showCheckboxes && (rowChecked || rowHovered) && (
                        <input
                          type="checkbox"
                          checked={checkedItems.includes(
                            `${file.id}-local-file`
                          )}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => {
                            e.stopPropagation();
                            onToggleChecked(`${file.id}-local-file`);
                          }}
                          aria-label={`Select ${file.filename}`}
                        />
                      )}
                    </td>

                    <td
                      className={`py-3 px-4 ${
                        rowChecked && showCheckboxes ? "font-bold" : ""
                      }`}
                    >
                      <div className="flex text-slate-700 items-center my-1 space-x-3">
                        {loadingFileId === file.id ? (
                          <Loader size="small" color="#9BA2AE" />
                        ) : (
                          <img
                            src={getFileIcon(file.filetype)}
                            alt={`${file.filetype} icon`}
                            className="w-6 h-6 2xl:w-7 2xl:h-7"
                          />
                        )}

                        <div className="relative flex-1">
                          <button
                            type="button"
                            className={[
                              "flex space-x-2 w-full text-left",
                              interactive
                                ? "cursor-pointer hover:underline"
                                : "cursor-wait text-gray-400",
                            ].join(" ")}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (interactive) {
                                onLoadFile(
                                  file.id,
                                  file.filetype,
                                  file?.size ?? 0,
                                  file.filename
                                );
                              }
                            }}
                          >
                            <div className="w-full truncate">
                              {file.filename}
                            </div>
                          </button>

                          {!interactive && (
                            <div className="absolute bottom-full left-[40px] transform -translate-x-1/2 mb-1 hidden group-hover:block z-20">
                              <div className="bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap shadow-lg">
                                Processing
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    <td
                      className={`py-3 px-4 text-slate-700 ${
                        rowChecked && showCheckboxes ? "font-bold" : ""
                      }`}
                    >
                      {file.filetype}
                    </td>

                    <td
                      className={`py-3 px-4 text-slate-700 ${
                        rowChecked && showCheckboxes ? "font-bold" : ""
                      }`}
                    >
                      {typeof file.size === "number"
                        ? `${(file.size / 1024).toFixed(2)} KB`
                        : "-"}
                    </td>

                    <td
                      className={`py-3 px-4 text-slate-700 ${
                        rowChecked && showCheckboxes ? "font-bold" : ""
                      }`}
                    >
                      {file.owner
                        ? `${file.owner.first_name ?? ""} ${
                            file.owner.last_name ?? ""
                          }`.trim() || "Unknown"
                        : "Unknown"}
                    </td>

                    <td
                      className={`py-3 px-4 text-slate-700 ${
                        rowChecked && showCheckboxes ? "font-bold" : ""
                      }`}
                    >
                      {file.uploaded_at ? normalizeDate(file.uploaded_at) : ""}
                    </td>

                    <td className="py-3 px-4 text-slate-700">
                      <div className="flex justify-center items-center">
                        {file.status === "failed" ? (
                          <Tooltip
                            content="File Processing Failed"
                            arrow={false}
                            className="cursor-not-allowed"
                          >
                            <div className="cursor-not-allowed">
                              <img
                                src="/status/x-circle.svg"
                                className="w-5 h-5 2xl:w-6 2xl:h-6 cursor-not-allowed"
                              />
                            </div>
                          </Tooltip>
                        ) : ["xlsx", "csv"].includes(
                            file.filetype.toLowerCase()
                          ) ? (
                          file.status === "ready" ? (
                            activeTaskFileIds.includes(file.id) ? (
                              <Tooltip
                                content="Looking for Anomalies"
                                arrow={false}
                              >
                                <Loader color="#7da4f8" />
                              </Tooltip>
                            ) : (
                              <StatusIcon
                                file={file}
                                handleShowPopup={handleShowPopup}
                                triggerStatusPopup={triggerStatusPopup}
                                clearStatusPopup={clearStatusPopup}
                              />
                            )
                          ) : (
                            <Loader color="#7da4f8" />
                          )
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}

              {filteredFolders.length === 0 &&
                filteredFiles.length === 0 &&
                !isFolderCreating && (
                  <tr>
                    <td
                      colSpan={7}
                      className="py-6 px-4 text-center text-gray-500"
                    >
                      no item found
                    </td>
                  </tr>
                )}
            </tbody>
          </table>

          {totalPages > 1 &&
            Pagination &&
            onPageChange &&
            breadcrumbs.length === 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={onPageChange}
                containerClassName="my-[1rem] 2xl:my-[1rem]"
              />
            )}
        </>
      )}
    </div>
  );
}
